import React, { useMemo, useCallback, useState, useEffect, useRef, useLayoutEffect } from "react";
import { Dropdown } from "react-bootstrap";
import { registerLocale } from "react-datepicker";
import { id } from "date-fns/locale";
import "react-datepicker/dist/react-datepicker.css";

import { useCanvasHandler } from "hooks/useCanvasHandler";
import Swal from "sweetalert2";
import CreateSchedulePage from "./create-schedule-page";
import GenerateScheduleFromPatternPage from "./generate-schedule-from-pattern-page";
import BatchAssignSchedulePage from "./batch-assign-schedule-page";
import BatchUpdateCellsPage from "./batch-update-cells-page";
import PrintJson from "utils/printJson";
import { getContrastColor, formatDateToLocal } from "utils/utils";
import { postRequest, getRequest } from "services/api";
import {
  IconUsers,
  IconPlus,
  IconWand,
  IconStack2,
  IconInfoCircle,
  IconCalendarCheck,
  IconHandClick,
  IconGripVertical
} from "@tabler/icons-react";
import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { UserProvider, useUser } from "contexts/v1/masterData/UserContext";
import { ShiftsProvider, useShifts } from "../../master/context/ShiftsContext";
import { useSchedule } from "../context/ScheduleContext";
import { SchedulePatternProvider } from "../context/SchedulePatternContext";
import CreateUserPage from "../../../user/components/create-page";
import { ProductCreateProvider } from "contexts/v1/masterData/ProductCreateContext";
import ExportScheduleExcel from "./export-schedule-excel";
import AttendanceHubToolbar from "./attendance-hub-toolbar";
import { useAttendance } from "contexts/v1/masterData/AttendanceContext";

registerLocale("id", id);

const parseDayOffToIndex = (dayStr) => {
  const lower = String(dayStr || "").toLowerCase();
  if (!lower) return -1;
  if (lower.includes("minggu") || lower.includes("sunday")) return 0;
  if (lower.includes("senin") || lower.includes("monday")) return 1;
  if (lower.includes("selasa") || lower.includes("tuesday")) return 2;
  if (lower.includes("rabu") || lower.includes("wednesday")) return 3;
  if (lower.includes("kamis") || lower.includes("thursday")) return 4;
  if (lower.includes("jumat") || lower.includes("jum'at") || lower.includes("friday")) return 5;
  if (lower.includes("sabtu") || lower.includes("saturday")) return 6;
  return -1;
};

const ScheduleCell = React.memo(({ date, user, schedule, isToday, onCellClick, getContrastColor, isSelected, isEven }) => {
  const isWeekend = date.getDay() === 0 || date.getDay() === 6;
  const active = isToday(date);

  const bgColor = useMemo(() => {
    if (schedule?.is_day_off) return "#ff8a8aff";
    if (schedule?.shift?.color) return schedule.shift.color;
    return isWeekend ? "#fcfdfe" : (isEven ? "white" : "#f1f5f9");
  }, [schedule, isWeekend, isEven]);

  const textColor = useMemo(() => {
    if (schedule?.is_day_off) return "#ffffff";
    if (schedule?.shift?.color) return getContrastColor(schedule.shift.color);
    return "inherit";
  }, [schedule, getContrastColor]);

  return (
    <div
      className={`p-1 border-end d-flex flex-column align-items-center justify-content-center cell-hover transition-all position-relative ${isSelected ? 'bg-primary-lt' : ''}`}
      style={{
        width: "var(--cell-width)",
        backgroundColor: isSelected ? undefined : bgColor,
        cursor: "pointer",
        color: textColor,
        boxShadow: isSelected ? "inset 0 0 0 3px #206bc4" : "none"
      }}
      onClick={(e) => onCellClick(e, user, date, schedule)}
    >
      {schedule ? (
        <div className="text-center w-100 px-0">
          {!schedule.is_day_off && (
            <div className="fw-bold" style={{ fontSize: "0.55rem", lineHeight: "1", color: textColor }}>
              {schedule.shift?.name || "No Shift"}
            </div>
          )}

          {schedule.is_day_off ? (
            <div className="badge bg-red text-white border-0 px-1 py-0 w-100" style={{ fontSize: "0.55rem" }}>OFF</div>
          ) : (
            " "
          )}

          {schedule.status === "draft" && (
            <div className="mt-0">
              <span className="badge bg-warning-lt text-warning border-0 px-1 py-0" style={{ fontSize: "0.45rem", textTransform: "uppercase" }}>
                {schedule.status}
              </span>
            </div>
          )}
        </div>
      ) : (
        <div className="text-muted opacity-25" style={{ fontSize: "0.55rem" }}>-</div>
      )}
    </div>
  );
});

// 🔹 Memoized Row Component
const ScheduleRow = React.memo(({ user, index, isEven, daysInRange, scheduleMap, apiStats, shifts, onCellClick, onUpdateUser, onSelectUserDayOff, onSelectUserRow, isToday, getContrastColor, selectedCellsForUser, activeTotalValues, dragListeners, dragAttributes }) => {
  const userSchedules = useMemo(() => scheduleMap.get(user.id), [scheduleMap, user.id]);
  const stats = useMemo(() => apiStats[user.id], [apiStats, user.id]);
  const isFullRowSelected = daysInRange.length > 0 && daysInRange.every((date) => selectedCellsForUser?.has(date.toDateString()));

  const bgRowClass = isEven ? "bg-white" : "bg-alt-row";

  return (
    <div className={`d-flex border-bottom hover-shadow-sm transition-all align-items-stretch ${bgRowClass}`} style={{ minHeight: "32px" }}>
      {/* No. Cell */}
      <div className={`p-1 border-end sticky-start d-flex align-items-center justify-content-center text-muted fw-bold ${bgRowClass}`} style={{ width: "35px", zIndex: 25, left: 0, fontSize: "0.7rem", cursor: "grab" }} {...dragListeners} {...dragAttributes}>
        <IconGripVertical size={14} className="me-1 opacity-50" />
        {index + 1}
      </div>

      <div className={`p-1 px-2 border-end sticky-start d-flex align-items-center gap-2 ${bgRowClass}`} style={{ width: "var(--name-width)", zIndex: 25, left: "35px" }}>
        <div className="overflow-hidden flex-grow-1">
          <div className="fw-bold text-dark text-truncate" style={{ fontSize: "0.7rem", cursor: "pointer" }} onClick={() => onUpdateUser(user)}>{user.name}</div>
        </div>
        <button
          type="button"
          className={`btn btn-xs ${isFullRowSelected ? "btn-primary" : "btn-outline-primary"} px-1 py-0 flex-shrink-0`}
          style={{ fontSize: "0.55rem", lineHeight: "1.1" }}
          title={isFullRowSelected ? "Batalkan semua pilihan di baris ini" : "Pilih semua tanggal di baris ini"}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onSelectUserRow(user);
          }}
        >
          All
        </button>
      </div>

      {/* Day Off Cell */}
      <div className={`p-1 border-end d-flex align-items-center justify-content-center fw-bold text-primary cursor-pointer ${bgRowClass}`} style={{
        width: "45px",
        zIndex: 25,
        left: "calc(35px + var(--name-width))",
        position: "sticky",
        fontSize: "0.7rem"
      }}
        onClick={() => onSelectUserDayOff(user)}
      >
        {user.day_off ? String(user.day_off).toUpperCase() : '-'}
      </div>

      {daysInRange.map((date) => {
        const dateKey = date.toDateString();
        const schedule = userSchedules?.get(dateKey);
        const isSelected = selectedCellsForUser && selectedCellsForUser.has(dateKey);
        return (
          <ScheduleCell
            key={dateKey}
            date={date}
            user={user}
            schedule={schedule}
            isToday={isToday}
            onCellClick={onCellClick}
            getContrastColor={getContrastColor}
            isSelected={isSelected}
            isEven={isEven}
          />
        );
      })}

      {/* Right Summary Row */}
      {activeTotalValues && activeTotalValues.length > 0 && (
        <div className="d-flex border-start bg-light-lt text-black" style={{ flexShrink: 0, width: `${activeTotalValues.length * 40}px` }}>
          {activeTotalValues.includes("DO") && (
            <div className="p-0 text-center text-black border-end fw-bold d-flex align-items-center justify-content-center"
              style={{ width: "40px", fontSize: "0.65rem", backgroundColor: "#f0f7ff", color: "#206bc4" }}>
              {stats?.libur || 0}
            </div>
          )}
          {shifts.data?.filter(shift => activeTotalValues.includes(shift.id)).map(shift => (
            <div
              key={shift.id}
              className="p-0 text-center text-black border-end fw-bold d-flex align-items-center justify-content-center"
              style={{ width: "40px", fontSize: "0.65rem" }}
            >
              {stats?.shiftCounts?.[shift.id] || 0}
            </div>
          ))}
        </div>
      )}
    </div>
  );
});

const LazyScheduleRow = (props) => {
  const { user } = props;
  const [isVisible, setIsVisible] = useState(false);
  const rowRef = useRef(null);

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: String(user.id) });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1060 : "auto",
    position: isDragging ? "relative" : "static",
  };

  const setRefs = (node) => {
    rowRef.current = node;
    setNodeRef(node);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "300px" }
    );
    if (rowRef.current) observer.observe(rowRef.current);
    return () => observer.disconnect();
  }, []);

  if (!isVisible && !isDragging) {
    return <div ref={setRefs} style={{ ...style, minHeight: "32px", borderBottom: "1px solid #eee" }}></div>;
  }
  return (
    <div ref={setRefs} style={style}>
      <ScheduleRow {...props} dragListeners={listeners} dragAttributes={attributes} />
    </div>
  );
};

const AttendanceHubScheduleTab = () => {
  const {
    schedules,
    filterOptions,
    setFilterOptions,
    handleSearch,
    refetch,
    PaginationComponent,
  } = useSchedule();

  const { filterOptions: attendanceFilterOptions, setFilterOptions: setAttendanceFilterOptions } = useAttendance();
  const { users, updateUserSorts, refetch: refetchUsers } = useUser();
  const [shifts, setShifts] = useState({ data: [] });

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const activeUser = users.data.find(u => String(u.id) === active.id);
      if (!activeUser) return;
      
      const deptId = activeUser.department_id || activeUser.department?.id || null;
      
      // Filter users in the same department and keep their current sort order
      const deptUsers = users.data.filter(u => (u.department_id || u.department?.id || null) === deptId);
      deptUsers.sort((a, b) => (a.sort || 0) - (b.sort || 0));

      const oldIndex = deptUsers.findIndex(u => String(u.id) === active.id);
      const newIndex = deptUsers.findIndex(u => String(u.id) === over.id);
      
      if (oldIndex !== -1 && newIndex !== -1) {
        // Extract the existing sort values for this department
        const availableSorts = deptUsers.map(u => u.sort || 0);
        
        // Reorder the users locally within the department
        const [movedItem] = deptUsers.splice(oldIndex, 1);
        deptUsers.splice(newIndex, 0, movedItem);
        
        // Re-assign the available sort values sequentially to the newly ordered array
        const reorderedUsers = deptUsers.map((u, i) => ({
          id: u.id,
          sort: availableSorts[i] // Use the exact same pool of sort numbers
        }));
        
        updateUserSorts(reorderedUsers);
      }
    }
  };

  useEffect(() => {
    const fetchAllShifts = async () => {
      try {
        const response = await getRequest("/ahr/shifts?all=1");
        if (response.success && response.data) {
          setShifts({ data: response.data });
        }
      } catch (err) {
        console.error("Failed to fetch all shifts", err);
      }
    };
    fetchAllShifts();
  }, []);

  const [selectedCellsMap, setSelectedCellsMap] = useState(new Map());
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [selectedTotals, setSelectedTotals] = useState(null);
  const [cellWidth, setCellWidth] = useState(45);
  const [nameWidth, setNameWidth] = useState(150);

  const scrollContainerRef = useRef(null);
  const scrollPosRef = useRef({ top: 0, left: 0 });

  const handleScroll = useCallback((e) => {
    if (e.target) {
      scrollPosRef.current = {
        top: e.target.scrollTop,
        left: e.target.scrollLeft
      };
    }
  }, []);

  useLayoutEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = scrollPosRef.current.top;
      scrollContainerRef.current.scrollLeft = scrollPosRef.current.left;
    }
  });

  const totalOptions = useMemo(() => {
    const opts = [{ value: "DO", label: "Day Off (DO)" }];
    if (shifts.data) {
      shifts.data.forEach(shift => {
        opts.push({ value: shift.id, label: shift.code || shift.name });
      });
    }
    return opts;
  }, [shifts.data]);

  useEffect(() => {
    if (selectedTotals === null && totalOptions.length > 1) {
      setSelectedTotals(totalOptions);
    }
  }, [totalOptions, selectedTotals]);

  const activeTotalValues = useMemo(() => {
    if (!selectedTotals) return [];
    return selectedTotals.map(opt => opt.value);
  }, [selectedTotals]);

  const { handleError, showCanvasHandler, handleSuccessToast } =
    useCanvasHandler();

  const handleCreate = useCallback((e, initialData = null) => {
    if (e) e.preventDefault();
    showCanvasHandler(
      "Create Schedule",
      <UserProvider roleFilter="user_hr">
        <ShiftsProvider>
          <CreateSchedulePage
            initialData={initialData}
            onSuccess={() => {
              handleSuccessToast(true, "Schedule created successfully");
              refetch();
            }}
            onError={handleError}
          />
        </ShiftsProvider>
      </UserProvider>,
    );
  }, [showCanvasHandler, handleSuccessToast, refetch, handleError]);

  const handleGenerateFromPattern = (e) => {
    e.preventDefault();
    showCanvasHandler(
      "Generate Schedules from Pattern",
      <UserProvider roleFilter="user_hr">
        <SchedulePatternProvider>
          <GenerateScheduleFromPatternPage
            onSuccess={() => {
              handleSuccessToast(true, "Schedules generated successfully");
              refetch();
            }}
            onError={handleError}
          />
        </SchedulePatternProvider>
      </UserProvider>,
    );
  };

  const handleBatchAssign = (e) => {
    if (e) e.preventDefault();
    if (selectedCellsMap.size > 0) {
      showCanvasHandler(
        "Update Selected Cells",
        <ShiftsProvider>
          <BatchUpdateCellsPage
            selectedCellsMap={selectedCellsMap}
            onSuccess={(successCount) => {
              if (successCount > 0) {
                handleSuccessToast(true, `Successfully updated ${successCount} cells.`);
                setSelectedCellsMap(new Map());
                refetch();
              }
            }}
            onError={handleError}
          />
        </ShiftsProvider>
      );
    } else {
      showCanvasHandler(
        "Batch Assign Schedules",
        <UserProvider roleFilter="user_hr">
          <ShiftsProvider>
            <BatchAssignSchedulePage
              initialUserIds={[]}
              onSuccess={() => {
                handleSuccessToast(true, "Schedules assigned successfully");
                refetch();
              }}
              onError={handleError}
            />
          </ShiftsProvider>
        </UserProvider>,
      );
    }
  };

  const handleUpdate = useCallback((e, data) => {
    if (e) e.preventDefault();
    showCanvasHandler(
      "Update Schedule",
      <UserProvider roleFilter="user_hr">
        <ShiftsProvider>
          <SchedulePatternProvider>
            <CreateSchedulePage
              id={data.id}
              initialData={data}
              onSuccess={() => {
                handleSuccessToast(true, "Schedule updated successfully");
                refetch();
              }}
              onError={handleError}
            />
          </SchedulePatternProvider>
        </ShiftsProvider>
      </UserProvider>,
    );
  }, [showCanvasHandler, handleSuccessToast, refetch, handleError]);

  const handleUpdateUser = useCallback((user) => {
    showCanvasHandler(
      "Update User Info",
      <ProductCreateProvider>
        <CreateUserPage
          id={user.id}
          initialData={user}
          onSuccess={() => {
            handleSuccessToast(true, "User updated successfully");
            refetchUsers(); // Refetch users data (not just schedules) to update UI
          }}
          onError={handleError}
        />
      </ProductCreateProvider>
    );
  }, [showCanvasHandler, handleSuccessToast, refetchUsers, handleError]);

  const handleCellClick = useCallback((e, user, date, schedule) => {
    if (isSelectMode || e.ctrlKey || e.metaKey) {
      e.preventDefault();
      const dateKey = date.toDateString();
      const cellKey = `${user.id}_${dateKey}`;
      setSelectedCellsMap(prev => {
        const newMap = new Map(prev);
        if (newMap.has(cellKey)) {
          newMap.delete(cellKey);
        } else {
          newMap.set(cellKey, { user, date, schedule });
        }
        return newMap;
      });
      return;
    }

    if (schedule) {
      handleUpdate(e, schedule);
    } else {
      handleCreate(e, {
        user_id: user.id,
        schedule_date: formatDateToLocal(date),
        status: "draft"
      });
    }
  }, [handleUpdate, handleCreate, isSelectMode]);

  const handleColumnToggle = useCallback((date) => {
    const dateKey = date.toDateString();
    setSelectedCellsMap(prev => {
      const newMap = new Map(prev);
      for (const key of prev.keys()) {
        if (key.endsWith(`_${dateKey}`)) {
          newMap.delete(key);
        }
      }
      return newMap;
    });
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("id-ID", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const handleBatchPublish = async () => {
    const result = await Swal.fire({
      title: "Publish All Drafts?",
      text: `This will publish all draft schedules from ${formatDate(filterOptions.startDate)} to ${formatDate(filterOptions.endDate)} for the visible employees.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#206bc4",
      cancelButtonColor: "#95a5a6",
      confirmButtonText: "Yes, publish them!",
    });

    if (result.isConfirmed) {
      try {
        const payload = {
          start_date: formatDateToLocal(filterOptions.startDate),
          end_date: formatDateToLocal(filterOptions.endDate),
          user_ids: (users.data || []).map(u => u.id)
        };

        const response = await postRequest("/ahr/schedules/publish-batch", payload);

        if (response.success) {
          Swal.fire({
            icon: "success",
            title: "Success",
            text: response.message || "Batch publish successful",
            timer: 2000,
            showConfirmButton: false
          });
          refetch();
        } else {
          throw new Error(response.message || "Failed to batch publish schedules");
        }
      } catch (error) {
        handleError(error);
      }
    }
  };
  const getDaysInRange = (start, end) => {
    const days = [];
    const current = new Date(start);
    const last = new Date(end);

    while (current <= last) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    return days;
  };

  const isToday = (date) => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  const daysInRange = useMemo(() => {
    if (!filterOptions.startDate || !filterOptions.endDate) return [];
    return getDaysInRange(filterOptions.startDate, filterOptions.endDate);
  }, [filterOptions.startDate, filterOptions.endDate]);

  const gridMinWidth = useMemo(() => {
    const calendarWidth = daysInRange.length * cellWidth;
    const stickyLeftWidth = 35 + nameWidth + 45; // No + Employees + WK DO
    const stickyRightWidth = activeTotalValues.length * 40; // Selected columns
    return calendarWidth + stickyLeftWidth + stickyRightWidth;
  }, [daysInRange.length, activeTotalValues.length, cellWidth, nameWidth]);

  // Group schedules by user and date
  const scheduleMap = useMemo(() => {
    const map = new Map();

    schedules.data?.forEach((schedule) => {
      const userId = schedule.user_id;
      const dateKey = new Date(schedule.schedule_date).toDateString();

      if (!map.has(userId)) {
        map.set(userId, new Map());
      }

      map.get(userId).set(dateKey, schedule);
    });

    return map;
  }, [schedules.data]);

  // Get unique users from schedules (no duplicates)
  const usersData = useMemo(() => {
    let result = users.data || [];
    if (attendanceFilterOptions.departmentId) {
      result = result.filter((user) => String(user.department?.id) === String(attendanceFilterOptions.departmentId));
    }
    if (attendanceFilterOptions.userId) {
      result = result.filter((user) => String(user.id) === String(attendanceFilterOptions.userId));
    }
    return result;
  }, [users.data, attendanceFilterOptions.departmentId, attendanceFilterOptions.userId]);

  // Group users by department_id (name)
  const groupedUsers = useMemo(() => {
    const groups = {};
    usersData.forEach(user => {
      const deptName = user.department?.name || "Unassigned";
      if (!groups[deptName]) {
        groups[deptName] = [];
      }
      groups[deptName].push(user);
    });
    // Sort department names
    return Object.keys(groups).sort().reduce((acc, key) => {
      acc[key] = groups[key];
      return acc;
    }, {});
  }, [usersData]);

  const filteredGroupedUsers = groupedUsers;

  const handleDateChange = (date, type) => {
    setFilterOptions((prev) => ({
      ...prev,
      [type]: date,
      currentPage: 1
    }));
  };

  const handleGoToToday = () => {
    const now = new Date();
    const currentDay = now.getDate();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    
    let start, end;
    if (currentDay >= 26) {
      start = new Date(currentYear, currentMonth, 26);
      end = new Date(currentYear, currentMonth + 1, 25);
    } else {
      start = new Date(currentYear, currentMonth - 1, 26);
      end = new Date(currentYear, currentMonth, 25);
    }

    setFilterOptions(prev => ({
      ...prev,
      startDate: start,
      endDate: end,
      currentPage: 1
    }));
  };

  const handlePrevPeriod = () => {
    if (!filterOptions.startDate) return;
    const start = new Date(filterOptions.startDate);
    start.setMonth(start.getMonth() - 1);
    
    const end = new Date(start);
    end.setMonth(end.getMonth() + 1);
    end.setDate(25);
    
    setFilterOptions(prev => ({
      ...prev,
      startDate: start,
      endDate: end
    }));
  };

  const handleNextPeriod = () => {
    if (!filterOptions.startDate) return;
    const start = new Date(filterOptions.startDate);
    start.setMonth(start.getMonth() + 1);
    
    const end = new Date(start);
    end.setMonth(end.getMonth() + 1);
    end.setDate(25);
    
    setFilterOptions(prev => ({
      ...prev,
      startDate: start,
      endDate: end
    }));
  };

  const handleQuickSelectWKDO = useCallback(() => {
    if (!users.data || users.data.length === 0) return;
    
    setIsSelectMode(true);

    let selectedCount = 0;
    
    setSelectedCellsMap(prev => {
      const newMap = new Map(prev);
      users.data.forEach(user => {
        const userDoIndex = parseDayOffToIndex(user.day_off);
        if (userDoIndex === -1) return;
        
        daysInRange.forEach(date => {
          if (date.getDay() === userDoIndex) {
            const dateKey = date.toDateString();
            const cellKey = `${user.id}_${dateKey}`;
            const schedule = scheduleMap.get(user.id)?.get(dateKey);
            newMap.set(cellKey, { user, date, schedule });
            selectedCount++;
          }
        });
      });
      return newMap;
    });
    
    if (selectedCount > 0) {
      handleSuccessToast(true, `Auto-selected ${selectedCount} cells based on WK DO`);
    } else {
      handleSuccessToast(false, "No matching WK DO cells found in this range");
    }
  }, [users.data, daysInRange, scheduleMap, handleSuccessToast]);

  const handleSelectUserDayOff = useCallback((user) => {
    const userDoIndex = parseDayOffToIndex(user.day_off);
    if (userDoIndex === -1) {
      handleSuccessToast(false, `No valid WK DO for ${user.name}`);
      return;
    }
    
    setIsSelectMode(true);
    let selectedCount = 0;
    
    setSelectedCellsMap(prev => {
      const newMap = new Map(prev);
      daysInRange.forEach(date => {
        if (date.getDay() === userDoIndex) {
          const dateKey = date.toDateString();
          const cellKey = `${user.id}_${dateKey}`;
          const schedule = scheduleMap.get(user.id)?.get(dateKey);
          newMap.set(cellKey, { user, date, schedule });
          selectedCount++;
        }
      });
      return newMap;
    });
    
    if (selectedCount > 0) {
      handleSuccessToast(true, `Selected ${selectedCount} days off for ${user.name}`);
    }
  }, [daysInRange, scheduleMap, handleSuccessToast]);

  const handleSelectUserRow = useCallback((user) => {
    if (!daysInRange.length) {
      handleSuccessToast(false, "No dates available to select");
      return;
    }

    setIsSelectMode(true);

    const rowIsFullySelected = daysInRange.every((date) => {
      const dateKey = date.toDateString();
      return selectedCellsMap.has(`${user.id}_${dateKey}`);
    });

    const affectedCount = rowIsFullySelected
      ? daysInRange.filter((date) => selectedCellsMap.has(`${user.id}_${date.toDateString()}`)).length
      : daysInRange.filter((date) => !selectedCellsMap.has(`${user.id}_${date.toDateString()}`)).length;

    setSelectedCellsMap(prev => {
      const newMap = new Map(prev);

      if (rowIsFullySelected) {
        daysInRange.forEach(date => {
          const dateKey = date.toDateString();
          const cellKey = `${user.id}_${dateKey}`;
          newMap.delete(cellKey);
        });
        return newMap;
      }

      daysInRange.forEach(date => {
        const dateKey = date.toDateString();
        const cellKey = `${user.id}_${dateKey}`;
        const schedule = scheduleMap.get(user.id)?.get(dateKey);
        newMap.set(cellKey, { user, date, schedule });
      });
      return newMap;
    });

    if (rowIsFullySelected) {
      handleSuccessToast(true, `Cancelled ${affectedCount} selected cells for ${user.name}`);
    } else {
      handleSuccessToast(true, `Selected ${affectedCount} cells for ${user.name}`);
    }
  }, [daysInRange, scheduleMap, selectedCellsMap, handleSuccessToast]);

  const getScheduleForUserAndDate = (userId, date) => {
    const dateKey = date.toDateString();
    const userSchedules = scheduleMap.get(userId);
    return userSchedules?.get(dateKey);
  };

  // Process statistics from API
  const apiStats = useMemo(() => {
    // Sanity check for existence of statistics data
    const userSummaries = schedules.statistics?.user_summary || [];
    if (userSummaries.length === 0) return {};

    const stats = {};
    userSummaries.forEach(summary => {
      // Use string IDs for consistent mapping
      const userId = String(summary.user_id);

      stats[userId] = {
        libur: summary.total_day_off || 0,
        shiftCounts: {}
      };

      // Initialize all master shifts with 0
      const masterShifts = shifts.data || [];
      masterShifts.forEach(shift => {
        stats[userId].shiftCounts[String(shift.id)] = 0;
      });

      // Update with actual counts from API
      if (summary.shift_counts) {
        summary.shift_counts.forEach(sc => {
          const shiftId = String(sc.shift_id);
          stats[userId].shiftCounts[shiftId] = sc.count || 0;
        });
      }
    });
    return stats;
  }, [schedules.statistics, shifts.data]);

  if (schedules.loading || users.loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "400px" }}
      >
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (schedules.error) {
    return (
      <div className="p-3">
        <div className="alert alert-danger">
          Error loading schedule data: {schedules.error}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="p-3">
        <AttendanceHubToolbar
          startDate={filterOptions.startDate}
          endDate={filterOptions.endDate}
          departmentId={attendanceFilterOptions.departmentId}
          userId={attendanceFilterOptions.userId}
          onDateChange={handleDateChange}
          onPreviousPeriod={handlePrevPeriod}
          onNextPeriod={handleNextPeriod}
          onToday={handleGoToToday}
          onFilterChange={(changes) => setAttendanceFilterOptions((previous) => ({ ...previous, ...changes, currentPage: 1 }))}
          adjustControls={(
            <>
              <label className="form-label small fw-bold text-muted mb-1">Employee Column ({nameWidth}px)</label>
              <input type="range" className="form-range mb-3" min="100" max="300" step="5" value={nameWidth} onChange={(event) => setNameWidth(Number(event.target.value))} />
              <label className="form-label small fw-bold text-muted mb-1">Daily Column ({cellWidth}px)</label>
              <input type="range" className="form-range mb-0" min="30" max="100" step="5" value={cellWidth} onChange={(event) => setCellWidth(Number(event.target.value))} />
            </>
          )}
          extraControls={(
            <>
              <Dropdown autoClose="outside">
                  <Dropdown.Toggle 
                    variant="light" 
                    size="sm" 
                    className="border fw-bold text-secondary px-3 rounded"
                    style={{ minHeight: "31px", fontSize: "0.85rem" }}
                  >
                    Columns ({selectedTotals?.length || 0})
                  </Dropdown.Toggle>
                  <Dropdown.Menu style={{ maxHeight: "300px", overflowY: "auto", minWidth: "220px", zIndex: 1035 }}>
                    <div className="px-3 py-2 text-muted fw-bold d-flex justify-content-between align-items-center" style={{ fontSize: "0.75rem", letterSpacing: "0.05em" }}>
                      <span>SHOW COLUMNS</span>
                      {selectedTotals?.length < totalOptions.length ? (
                        <span className="text-primary cursor-pointer" onClick={() => setSelectedTotals(totalOptions)}>All</span>
                      ) : (
                        <span className="text-danger cursor-pointer" onClick={() => setSelectedTotals([])}>None</span>
                      )}
                    </div>
                    {totalOptions.map(opt => {
                      const isChecked = selectedTotals && selectedTotals.some(s => s.value === opt.value);
                      return (
                        <label 
                          key={opt.value}
                          className="dropdown-item d-flex align-items-center gap-2 cursor-pointer transition-all mb-0"
                          style={{ padding: "0.4rem 1rem" }}
                        >
                          <input 
                            type="checkbox" 
                            className="form-check-input m-0 cursor-pointer"
                            checked={isChecked || false}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedTotals([...(selectedTotals || []), opt]);
                              } else {
                                setSelectedTotals(selectedTotals.filter(s => s.value !== opt.value));
                              }
                            }}
                          />
                          <span style={{ fontSize: "0.85rem" }}>{opt.label}</span>
                        </label>
                      )
                    })}
                  </Dropdown.Menu>
                </Dropdown>
                <ExportScheduleExcel
                  daysInRange={daysInRange}
                  groupedUsers={filteredGroupedUsers}
                  scheduleMap={scheduleMap}
                  apiStats={apiStats}
                  shifts={shifts}
                  filterOptions={filterOptions}
                />
            </>
          )}
          actionControls={(
            <>
              <button className={`btn btn-sm ${isSelectMode ? "btn-primary" : "btn-outline-primary"} fw-bold`} onClick={() => setIsSelectMode(!isSelectMode)}>
                <IconHandClick size={15} className="me-1" />
                {isSelectMode ? "Select ON" : "Select OFF"}
              </button>
              <button className="btn btn-sm btn-outline-primary fw-bold" onClick={handleQuickSelectWKDO} title="Select all days matching employee's WK DO">
                <IconWand size={15} className="me-1" /> WK DO
              </button>
              {selectedCellsMap.size > 0 && (
                <button className="btn btn-sm btn-outline-danger fw-bold" onClick={() => setSelectedCellsMap(new Map())}>
                  Clear
                </button>
              )}
              <button className={`btn btn-sm ${selectedCellsMap.size > 0 ? "btn-warning" : "btn-outline-warning"} fw-bold`} onClick={handleBatchAssign}>
                <IconStack2 size={15} className="me-1" />
                {selectedCellsMap.size > 0 ? `Update (${selectedCellsMap.size})` : "Batch"}
              </button>
              <button className="btn btn-sm btn-outline-success fw-bold" onClick={handleGenerateFromPattern}>
                <IconWand size={15} className="me-1" /> Generate
              </button>
              <button className="btn btn-sm btn-outline-info fw-bold" onClick={handleBatchPublish}>
                <IconCalendarCheck size={15} className="me-1" /> Publish
              </button>
              <button className="btn btn-sm btn-primary shadow-sm px-3 fw-bold" onClick={handleCreate}>
                <IconPlus size={15} className="me-1" /> Create Schedule
              </button>
            </>
          )}
        />

        {/* 🔹 Industrial Standard Schedule Grid */}
        <div className="card border-0 shadow-sm overflow-hidden" style={{ "--cell-width": `${cellWidth}px`, "--name-width": `${nameWidth}px` }}>
          <div 
            className="table-responsive" 
            style={{ maxHeight: "750px" }}
            ref={scrollContainerRef}
            onScroll={handleScroll}
          >
            <div style={{ minWidth: `${gridMinWidth}px` }}>
              {/* Header Row */}
              <div className="d-flex sticky-top border-bottom bg-white" style={{ zIndex: 30, top: 0 }}>
                <div className="p-2 fw-bold text-uppercase ls-1 text-muted border-end sticky-start bg-white d-flex align-items-center justify-content-center" style={{
                  width: "35px",
                  fontSize: "0.6rem",
                  zIndex: 35,
                  left: 0,
                  position: "sticky",
                  top: 0
                }}>
                  NO.
                </div>
                <div className="p-2 fw-bold text-uppercase ls-1 text-muted border-end sticky-start bg-white d-flex align-items-center gap-2" style={{
                  width: "var(--name-width)",
                  fontSize: "0.6rem",
                  zIndex: 35,
                  left: "35px",
                  position: "sticky",
                  top: 0
                }}>
                  <IconUsers size={12} /> Employees
                </div>

                {/* Day Off Column */}
                <div className="p-2 sticky-top fw-bold text-uppercase ls-1 text-muted border-end bg-white d-flex align-items-center justify-content-center text-center" style={{
                  width: "45px",
                  fontSize: "0.5rem",
                  zIndex: 35,
                  position: "sticky",
                  left: "calc(35px + var(--name-width))",
                  top: 0
                }}>
                  WK DO
                </div>
                {daysInRange.map((date, index) => {
                  const dayName = ["Min", "Sen", "Sel", "Rab", "Kam", "Jum", "Sab"][date.getDay()];
                  const isWeekend = date.getDay() === 0 || date.getDay() === 6;
                  const active = isToday(date);
                  return (
                    <div
                      key={index}
                      className={`p-1 text-center border-end d-flex flex-column justify-content-center transition-all sticky-top ${active ? "bg-primary-lt" : ""}`}
                      style={{
                        width: "var(--cell-width)",
                        zIndex: 30,
                        top: 0,
                        position: "sticky",
                        // color: isWeekend ? "#9ca3af" : "inherit",
                        backgroundColor: "#66b73eff",
                        cursor: "pointer"
                      }}
                      onClick={() => handleColumnToggle(date)}
                      title="Click to deselect all cells in this column"
                    >
                      <div style={{ fontSize: "0.55rem", textTransform: "uppercase", fontWeight: 600 }}>{dayName}</div>
                      <div className={`fw-bold ${active ? "text-primary transition-all scale-110" : ""}`} style={{ fontSize: "0.85rem" }}>{date.getDate()}</div>
                      {active && <div className="mx-auto mt-1 rounded bg-primary" style={{ width: "3px", height: "3px" }}></div>}
                    </div>
                  );
                })}

                {/* Right Summary Header */}
                {activeTotalValues && activeTotalValues.length > 0 && (
                  <div className="d-flex flex-column border-start sticky-top" style={{ flexShrink: 0, width: `${activeTotalValues.length * 40}px`, zIndex: 30, top: 0, position: "sticky" }}>
                    <div className="p-1 fw-bold text-center border-bottom text-white flex-shrink-0" style={{ fontSize: "0.6rem", backgroundColor: "#66b73eff" }}>
                      TOTAL
                    </div>
                    <div className="d-flex flex-grow-1">
                      {activeTotalValues.includes("DO") && (
                        <div className="p-0 text-center border-end fw-bold d-flex align-items-center justify-content-center text-white"
                          style={{ width: "40px", fontSize: "0.5rem", backgroundColor: "#206bc4" }}>
                          DO
                        </div>
                      )}
                      {shifts.data?.filter(shift => activeTotalValues.includes(shift.id)).map(shift => (
                        <div
                          key={shift.id}
                          className="p-0 text-center border-end fw-bold d-flex align-items-center justify-content-center"
                          style={{
                            width: "40px",
                            fontSize: "0.5rem",
                            backgroundColor: shift.color || "#eee",
                            color: shift.color ? getContrastColor(shift.color) : "inherit"
                          }}
                        >
                          {shift.code || shift.name}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Body Rows */}
              <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              {Object.keys(filteredGroupedUsers).length > 0 ? (() => {
                let globalUserIndex = 0;
                return Object.entries(filteredGroupedUsers).map(([deptName, deptUsers]) => (
                  <React.Fragment key={deptName}>
                    {/* Department Sticky Header Row */}
                    <div className="d-flex bg-primary-lt border-bottom" style={{ zIndex: 20 }}>
                      <div className="p-2 fw-bold text-primary d-flex align-items-center gap-2 sticky-start" style={{
                        fontSize: "0.75rem",
                        letterSpacing: "0.02em",
                        position: "sticky",
                        left: 0,
                        zIndex: 25,
                        backgroundColor: "#f4f8fb"
                      }}>
                        <IconStack2 size={14} /> {deptName.toUpperCase()}
                      </div>
                    </div>

                    <SortableContext items={deptUsers.map(u => String(u.id))} strategy={verticalListSortingStrategy}>
                    {deptUsers.map((user) => {
                      const userCells = Array.from(selectedCellsMap.values()).filter(c => c.user.id === user.id);
                      const selectedCellsForUser = new Set(userCells.map(c => c.date.toDateString()));
                      const currentIndex = globalUserIndex++;
                      const isEven = currentIndex % 2 === 0;

                      return (
                        <LazyScheduleRow
                          key={user.id}
                          user={user}
                          index={currentIndex}
                          isEven={isEven}
                          daysInRange={daysInRange}
                          scheduleMap={scheduleMap}
                          apiStats={apiStats}
                          shifts={shifts}
                          onCellClick={handleCellClick}
                          onUpdateUser={handleUpdateUser}
                          onSelectUserDayOff={handleSelectUserDayOff}
                          onSelectUserRow={handleSelectUserRow}
                          isToday={isToday}
                          getContrastColor={getContrastColor}
                          selectedCellsForUser={selectedCellsForUser}
                          activeTotalValues={activeTotalValues}
                        />
                      );
                    })}
                    </SortableContext>
                  </React.Fragment>
                ));
              })() : (
                <div className="p-5 text-center text-muted border-bottom bg-light bg-opacity-50">
                  <IconInfoCircle size={48} className="mb-3 opacity-20" />
                  <div className="fw-bold">No schedule data found for this range</div>
                  <div className="small">Try adjusting your filters or search terms</div>
                </div>
              )}
              </DndContext>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .schedule-nav-card {
          box-shadow: 0 8px 24px rgba(0,0,0,0.08) !important;
          border: 1px solid rgba(231, 234, 239, 0.5) !important;
        }
        .cell-hover:hover {
          background-color: #f1f5f9 !important;
          z-index: 5;
          transform: scale(1.02);
          box-shadow: 0 4px 12px rgba(0,0,0,0.05);
        }
        .scale-110 {
          transform: scale(1.15);
        }
        .ls-1 {
          letter-spacing: 0.05em;
        }
        .bg-alt-row {
          background-color: #f1f5f9 !important;
        }
        .bg-primary-lt {
          background-color: rgba(32, 107, 196, 0.08) !important;
        }
        .sticky-start {
          position: sticky;
          left: 0;
          box-shadow: 4px 0 10px -4px rgba(0,0,0,0.1);
        }
      `}</style>

      <PrintJson data={schedules} title="Schedule Data" />
    </>
  );
};

export default AttendanceHubScheduleTab;
